import axios from "@axios";

const MAP_UNITS_URL = "/v1/services/map/units";
const TENANTS_URL = "/v1/services/tenants/tenants-by-user?type=map";
const MAP_UNITS_PAGE_SIZE = 500;

let unitsRequest = null;

const fetchTenants = async (params) => {
  const { data } = await axios.get(TENANTS_URL, { params });

  return Array.isArray(data?.data) ? data.data : [];
};

const fetchMapUnits = async (params = {}) => {
  const units = [];
  const visitedUrls = new Set();
  let nextUrl = MAP_UNITS_URL;
  let requestConfig = {
    params: {
      max: params.max || MAP_UNITS_PAGE_SIZE,
      ...(params.id_tenant ? { id_tenant: params.id_tenant } : {}),
    },
  };

  while (nextUrl) {
    if (visitedUrls.has(nextUrl)) {
      throw new Error(`Repeated cursor URL while loading map units: ${nextUrl}`);
    }

    visitedUrls.add(nextUrl);

    const { data: payload } = await axios.get(nextUrl, requestConfig);

    if (!Array.isArray(payload?.data)) {
      throw new Error("Invalid map units response");
    }

    units.push(...payload.data);
    nextUrl = payload.links?.next || null;
    requestConfig = undefined;
  }

  return units;
};

const fetchLegacyTenantUnits = async (tenant) => {
  const units = [];
  let page = 1;
  let lastPage = 1;

  do {
    const { data: payload } = await axios.get(tenant.units, {
      params: {
        max: MAP_UNITS_PAGE_SIZE,
        "page[n]": page,
      },
    });

    units.push(...(Array.isArray(payload?.data) ? payload.data : []));
    lastPage = Number(payload?.meta?.last_page || 1);
    page += 1;
  } while (page <= lastPage);

  return units;
};

const fetchLegacyMapUnits = async (tenants) => {
  const results = await Promise.allSettled(
    tenants.map((tenant) => fetchLegacyTenantUnits(tenant))
  );
  const failedCount = results.filter((result) => result.status === "rejected").length;

  if (failedCount > 0) {
    console.warn(`${failedCount} tenants could not be loaded.`);
  }

  return results
    .filter((result) => result.status === "fulfilled")
    .flatMap((result) => result.value);
};

const shouldUseLegacyFallback = (error) => [404, 405].includes(error?.response?.status);

const deduplicateUnits = (units) => Array.from(
  new Map(
    units
      .filter((unit) => unit?.id != null)
      .map((unit) => [String(unit.id), unit])
  ).values()
);

const state = {
  tenants: [],
  tenantsFilter: [],
  units: [],
  unitsFilter: [],
  unitsVersion: 1,
  loadingUnits: false,
};

const getters = {
  getTenants(state) {
    let tenants = [...state.tenantsFilter].sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
    return tenants;
  },
  getUnits: (state) => (filters) => {
    let units = state.unitsFilter.filter((unit) => {
      if (filters.idUnit) {
        return unit.id_tenant === filters.idTenant && unit.id === filters.idUnit;
      }
      return unit.id_tenant === filters.idTenant;
    });

    if (filters.filter && filters.filter.length > 0) {
      units = units.filter((unit) => filters.filter.includes(unit.id));
    }

    return units.slice(
      (filters.currentPage - 1) * filters.perPage,
      filters.currentPage * filters.perPage
    );
  },
  getTotalUnits: (state) => (filters) => {
    let units = state.unitsFilter.filter((unit) => {
      if (filters.idUnit) {
        return unit.id_tenant === filters.idTenant && unit.id === filters.idUnit;
      }
      return unit.id_tenant === filters.idTenant;
    });

    if (filters.filter && filters.filter.length > 0) {
      units = units.filter((unit) => filters.filter.includes(unit.id));
    }

    return units.length;
  },
  getAllTenants: (state) => {
    return state.tenantsFilter;
  },
  getAllUnist: (state) => {
    return state.unitsFilter;
  },
};

const mutations = {
  // General
  setTenants(state, value) {
    state.tenants = value;
    state.tenantsFilter = value;
  },

  resetTenants(state) {
    state.tenants = [];
    state.tenantsFilter = [];
  },

  setTenantsFilter(state, value) {
    state.tenantsFilter = value;
  },

  setUnits(state, value) {
    state.units = value;
    state.unitsFilter = value;
  },

  addUnits(state, value) {
    state.units.push(...value);
    state.unitsFilter.push(...value);
  },

  resetUnits(state) {
    state.units = [];
    state.unitsFilter = [];
  },

  setUnitsFilter(state, value) {
    state.unitsFilter = value;
  },

  setUnitsLoadStatus(state, value) {
    state.loadingUnits = value;
  },

  updateUnitsVersion(state) {
    state.unitsVersion += 1;
  },
};

const actions = {
  getUnitsByTenant({commit, state}, params = {}) {
    if (unitsRequest) return unitsRequest;
    if (state.units.length && !params.force) return Promise.resolve(state.units);

    commit("setUnitsLoadStatus", true);

    const tenantRequest = state.tenants.length
      ? Promise.resolve({ tenants: state.tenants })
      : fetchTenants(params)
        .then((tenants) => {
          commit("setTenants", tenants);
          return { tenants };
        })
        .catch((error) => ({ error }));

    const request = (async () => {
      let units;

      try {
        units = await fetchMapUnits(params);
      } catch (error) {
        if (!shouldUseLegacyFallback(error)) throw error;

        console.warn("Map units endpoint unavailable; using legacy tenant requests.");
        const tenantResult = await tenantRequest;

        if (tenantResult.error) throw tenantResult.error;
        units = await fetchLegacyMapUnits(tenantResult.tenants);
      }

      const tenantResult = await tenantRequest;

      if (tenantResult.error) {
        console.warn("Tenants could not be loaded while map units were available.", tenantResult.error);
      }

      const uniqueUnits = deduplicateUnits(units);
      commit("setUnits", uniqueUnits);
      commit("setMaintenanceFromUnits", uniqueUnits);

      return uniqueUnits;
    })();

    unitsRequest = request;

    return request.finally(() => {
      if (unitsRequest === request) unitsRequest = null;
      commit("setUnitsLoadStatus", false);
    });
  },

  async resetUnits({ commit, dispatch }) {
    commit("resetUnits");
    const units = await dispatch("getUnitsByTenant", { force: true });
    commit("updateUnitsVersion");

    return units;
  },

  filterUnits({ commit, state }, { search }) {
    const normalizedSearch = String(search || "").toLowerCase();
    let units = state.units.filter((unit) => [
      unit.description,
      unit.address_1,
      unit.address_2,
      unit.suburb,
      unit.municipality?.name,
      unit.postal_code,
      unit.region?.name,
      unit.country?.name,
    ].some((value) => String(value || "").toLowerCase().includes(normalizedSearch)));

    commit("setUnitsFilter", units);

    let tenants = units.map((unit) => unit.id_tenant);

    let tenantsFilter = state.tenants.filter((tenant) => {
      return tenants.includes(tenant.id);
    });

    tenantsFilter.forEach((tenant) => {
      tenant.units_count = units.filter((unit) => {
        return unit.id_tenant === tenant.id;
      }).length;
    });

    commit("setTenantsFilter", tenantsFilter);
  },

  unfilterUnits({ state }) {
    state.unitsFilter = state.units;
    state.tenantsFilter = state.tenants;

    state.tenantsFilter.forEach((tenant) => {
      tenant.units_count = state.unitsFilter.filter((unit) => {
        return unit.id_tenant === tenant.id;
      }).length;
    });
  },

  updateUnitsVersion({ commit }) {
    commit("updateUnitsVersion");
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
